import { Button, type ButtonProps } from "@chakra-ui/react";
import { NAVBAR_GREEN_GRADIENT } from "./gradients";

interface NavbarCTAButtonProps extends Omit<ButtonProps, "onClick"> {
  children: React.ReactNode;
  onClick: () => void;
  scrolled: boolean;
  icon?: React.ReactNode;
}

/**
 * Navbar CTA pill button. Adapts its visual style based on scroll position:
 * - Not scrolled (hero/dark bg): white outline, transparent fill →
 *   hover: white fill + green text/icon + lift + glow
 * - Scrolled (white navbar bg): gradient green fill (matches DialogActionButton confirm) →
 *   hover: lift + stronger shadow
 *
 * Used for "Book Now" and "Sign In" in the homeowner navbar, and the CTA section.
 * Accepts extra ButtonProps via rest — callers can override h, px, fontSize, etc.
 */
export default function NavbarCTAButton({
  children,
  onClick,
  scrolled,
  icon,
  ...rest
}: NavbarCTAButtonProps) {
  return (
    <Button
      h="2.3rem"
      px="1.1rem"
      rounded="full"
      fontSize="0.875rem"
      fontWeight="700"
      letterSpacing="0.01em"
      gap="0.45rem"
      display="inline-flex"
      alignItems="center"
      bg={scrolled ? undefined : "transparent"}
      style={{ background: scrolled ? NAVBAR_GREEN_GRADIENT : undefined }}
      color="white"
      border="1px solid"
      borderColor={scrolled ? "transparent" : "rgba(255,255,255,0.72)"}
      boxShadow={scrolled ? "0 6px 16px rgba(34,197,94,0.24)" : "none"}
      transition="all 0.25s ease"
      onClick={onClick}
      _hover={
        scrolled
          ? {
              transform: "translateY(-1px)",
              boxShadow: "0 10px 24px rgba(34,197,94,0.32)",
            }
          : {
              bg: "white",
              color: "#16A34A",
              borderColor: "white",
              transform: "translateY(-1.5px)",
              boxShadow: "0 6px 20px rgba(255,255,255,0.28)",
            }
      }
      {...rest}
    >
      {children}
      {icon}
    </Button>
  );
}
